import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Clock, Package, Search, Trash2,
  AlertCircle, HardHat, LogOut, Send, ScanLine
} from 'lucide-react';
import { toast } from 'sonner';
import TimeTrackingPanel from './TimeTrackingPanel';

import { apiFetch } from '../../services/api';
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Mobile Web-App-Ansicht für Monteure („Mobiler Monteur").
 * Sichtbar nur wenn die Firma des Mitarbeiters Pro aktiv hat.
 *
 * 3 Kern-Masken (App-artig, One-Screen-Focus):
 *   • Kolonnen-Zeiterfassung
 *   • Material-Erfassung (Suche/Scan-Dummy)
 *   • Status-Meldungen (Krankmeldung/Sondervorkommnisse)
 */

const tabs = [
  { id: 'time', label: 'Zeit', icon: Clock },
  { id: 'material', label: 'Material', icon: Package },
  { id: 'status', label: 'Status', icon: AlertCircle },
];

const MonteurProApp = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('time');
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [proActive, setProActive] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await apiFetch(`${API}/api/pro/status`, { headers: {} });
        const sd = await s.json();
        if (!sd.active) {
          setProActive(false);
          return;
        }
        setProActive(true);
        const p = await apiFetch(`${API}/api/pro/projects`, { headers: {} });
        const pd = await p.json();
        setProjects(pd.projects || []);
      } catch (err) {
        // Nicht app-blockierend – wir zeigen halt keine Projekte
        console.warn('MonteurProApp: Init-Fetch fehlgeschlagen', err);
      }
    })();
  }, []);

  if (!proActive) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-4">
        <Card className="max-w-sm bg-gray-950 border-yellow-500/40">
          <CardContent className="p-6 text-center space-y-3">
            <HardHat className="h-12 w-12 text-yellow-500 mx-auto" />
            <h2 className="text-lg font-bold">Nur mit SmartVolt Pro</h2>
            <p className="text-gray-400 text-sm">
              Diese Baustellen-App ist Teil von SmartVolt Pro. Bitte den Chef, das Pro-Abo zu aktivieren.
            </p>
            <Button variant="outline" onClick={() => navigate('/mitarbeiter-cockpit')} className="border-gray-700 w-full">
              Zurück
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-white pb-24" data-testid="monteur-pro-app">
      {/* Sticky Mobile Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/80 border-b border-yellow-500/20">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HardHat className="h-5 w-5 text-yellow-500" />
            <span className="text-sm font-bold">SmartVolt Pro</span>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/mitarbeiter-cockpit')} className="text-gray-400" data-testid="monteur-back">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-4 space-y-4">
        {tab === 'time' && <TimeTrackingPanel />}
        {tab === 'material' && (
          <>
            <Card className="bg-gray-900/60 border-gray-800">
              <CardContent className="p-4">
                <Label className="text-xs text-gray-400 uppercase tracking-wider">Aktuelle Baustelle</Label>
                <select
                  value={selectedProject}
                  onChange={(e) => setSelectedProject(e.target.value)}
                  className="w-full mt-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white"
                  data-testid="monteur-project-select-material"
                >
                  <option value="">Baustelle wählen…</option>
                  {projects.map((p) => (
                    <option key={p._id} value={p._id}>{p.name}{p.address ? ` – ${p.address}` : ''}</option>
                  ))}
                </select>
              </CardContent>
            </Card>
            <MaterialCapturePanel projectId={selectedProject} />
          </>
        )}
        {tab === 'status' && <StatusReportPanel />}
      </main>

      {/* Mobile Bottom Tab-Bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 backdrop-blur-xl bg-black/90 border-t border-yellow-500/20">
        <div className="max-w-lg mx-auto grid grid-cols-3">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex flex-col items-center gap-1 py-3 text-xs transition-colors ${
                tab === t.id ? 'text-yellow-400' : 'text-gray-500 hover:text-gray-300'
              }`}
              data-testid={`monteur-tab-${t.id}`}
            >
              <t.icon className="h-5 w-5" />
              {t.label}
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

// ---------------------------------------------------------------------------
const MaterialCapturePanel = ({ projectId }) => {
  const [search, setSearch] = useState('');
  const [lines, setLines] = useState([]);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);

  // Live-Suche im Großhandels-Katalog (Debounced)
  useEffect(() => {
    if (!search.trim()) {
      setResults([]);
      setShowResults(false);
      return;
    }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await apiFetch(
          `${API}/api/pro/material/catalog/search?q=${encodeURIComponent(search)}&limit=8`,
          { headers: {} },
        );
        const data = await res.json();
        setResults(data.items || []);
        setShowResults(true);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => clearTimeout(t);
  }, [search]);

  const addFromCatalog = (item) => {
    const uid = `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setLines((prev) => [
      ...prev,
      {
        uid,
        article_number: item.article_number,
        description: item.description,
        quantity: 1,
        unit: item.unit || 'Stk',
        unit_price_eur: item.unit_price_eur || 0,
        supplier: item.supplier || null,
        barcode: item.barcode || null,
      },
    ]);
    setSearch('');
    setResults([]);
    setShowResults(false);
  };

  const addCustomLine = () => {
    if (!search.trim()) return;
    const uid = `line-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setLines((prev) => [...prev, {
      uid,
      article_number: `FREI-${Date.now().toString().slice(-4)}`,
      description: search,
      quantity: 1,
      unit: 'Stk',
      unit_price_eur: 0 }]);
    setSearch('');
    setResults([]);
    setShowResults(false);
  };

  const scanMock = async () => {
    // Mock-Scan: nimmt einen zufälligen Barcode aus dem Katalog
    const mockBarcodes = ['4001234500011', '4001234500073', '4001234500134', '4001234500196'];
    const bc = mockBarcodes[Math.floor(Math.random() * mockBarcodes.length)];
    try {
      const res = await apiFetch(`${API}/api/pro/material/catalog/barcode/${bc}`, { headers: {} });
      const data = await res.json();
      if (res.ok && data.item) {
        addFromCatalog(data.item);
        toast.success(`Gescannt: ${data.item.article_number}`);
      } else {
        toast.error('Barcode nicht gefunden');
      }
    } catch {
      toast.error('Scan fehlgeschlagen');
    }
  };

  const removeLine = (uid) => setLines((prev) => prev.filter((l) => l.uid !== uid));

  const send = async () => {
    if (!projectId) return toast.error('Bitte Baustelle wählen');
    if (lines.length === 0) return toast.error('Warenkorb ist leer');
    try {
      const res = await apiFetch(`${API}/api/pro/material/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project_id: projectId, lines }) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
      toast.success('An den Chef geschickt', { description: `${lines.length} Position(en) warten auf Freigabe.` });
      setLines([]);
    } catch (err) {
      toast.error(err.message || 'Fehler');
    }
  };

  const cartTotal = lines.reduce((s, l) => s + (l.quantity || 0) * (l.unit_price_eur || 0), 0);

  return (
    <Card className="bg-gray-900/60 border-gray-800" data-testid="monteur-material-panel">
      <CardContent className="p-4 space-y-4">
        <h3 className="text-white font-semibold flex items-center gap-2"><Package className="h-4 w-4 text-yellow-400" /> Material erfassen</h3>
        <p className="text-[11px] text-gray-500 -mt-2">
          Angebunden an Großhandels-Dummy · später umschaltbar auf Sonepar/Rexel/Regro
        </p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-500" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (results.length > 0) addFromCatalog(results[0]);
                  else addCustomLine();
                }
              }}
              placeholder="Artikel-Nr., EAN oder Bezeichnung…"
              className="pl-8 bg-gray-800 border-gray-700"
              data-testid="monteur-material-search"
              autoComplete="off"
            />
          </div>
          <Button variant="outline" onClick={scanMock} className="border-gray-700" data-testid="monteur-material-scan" title="Barcode simulieren">
            <ScanLine className="h-4 w-4" />
          </Button>
        </div>

        {showResults && (
          <div className="rounded-lg border border-yellow-500/20 bg-black/40 divide-y divide-gray-800 max-h-64 overflow-y-auto" data-testid="monteur-material-results">
            {searching && <div className="p-3 text-xs text-gray-500">Suche im Katalog…</div>}
            {!searching && results.length === 0 && (
              <div className="p-3 text-xs text-gray-500 flex items-center justify-between gap-2">
                <span>Kein Katalog-Treffer.</span>
                <Button size="sm" variant="ghost" onClick={addCustomLine} className="text-yellow-400 hover:text-yellow-300" data-testid="monteur-material-add-custom">
                  Als Freitext anlegen
                </Button>
              </div>
            )}
            {!searching && results.map((it) => (
              <button
                key={it.article_number}
                onClick={() => addFromCatalog(it)}
                className="w-full text-left p-3 hover:bg-yellow-500/5 transition-colors"
                data-testid={`monteur-material-result-${it.article_number}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-white font-medium truncate">{it.description}</span>
                  <span className="text-xs text-yellow-400 shrink-0">{it.unit_price_eur.toFixed(2)} €</span>
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[10px] text-gray-500 font-mono">{it.article_number}</span>
                  <span className="text-[10px] text-gray-600">·</span>
                  <span className="text-[10px] text-gray-500">{it.supplier}</span>
                  {it.category && (
                    <>
                      <span className="text-[10px] text-gray-600">·</span>
                      <span className="text-[10px] text-yellow-500/80">{it.category}</span>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {lines.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400 uppercase tracking-wider">Warenkorb ({lines.length})</p>
              <p className="text-xs text-yellow-400 font-semibold" data-testid="monteur-cart-total">
                {cartTotal.toFixed(2)} €
              </p>
            </div>
            {lines.map((ln, i) => (
              <div key={ln.uid} className="flex items-center gap-2 bg-black/40 border border-gray-800 rounded-lg p-2" data-testid={`monteur-cart-line-${i}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{ln.description}</p>
                  <p className="text-[10px] text-gray-500">
                    Art.-Nr. {ln.article_number}
                    {ln.supplier ? ` · ${ln.supplier}` : ''}
                    {ln.unit_price_eur ? ` · ${ln.unit_price_eur.toFixed(2)} € / ${ln.unit}` : ''}
                  </p>
                </div>
                <Input
                  type="number" min="1"
                  value={ln.quantity}
                  onChange={(e) => setLines((p) => p.map((l) => l.uid === ln.uid ? { ...l, quantity: parseFloat(e.target.value) || 1 } : l))}
                  className="w-16 bg-gray-800 border-gray-700 text-center"
                />
                <Button size="sm" variant="ghost" onClick={() => removeLine(ln.uid)} className="text-rose-400">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button onClick={send} className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold" data-testid="monteur-material-send">
              <Send className="h-4 w-4 mr-2" /> An Chef zur Freigabe
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
const StatusReportPanel = () => {
  const [kind, setKind] = useState('sick');
  const [note, setNote] = useState('');

  const send = async () => {
    try {
      const res = await apiFetch(`${API}/api/pro/hr/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, note }) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
      toast.success('Meldung gesendet');
      setNote('');
    } catch (err) {
      toast.error(err.message || 'Fehler');
    }
  };

  return (
    <Card className="bg-gray-900/60 border-gray-800" data-testid="monteur-status-panel">
      <CardContent className="p-4 space-y-4">
        <h3 className="text-white font-semibold flex items-center gap-2"><AlertCircle className="h-4 w-4 text-yellow-400" /> Status-Meldung</h3>
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: 'sick', label: 'Krank' },
            { id: 'vacation', label: 'Urlaub' },
            { id: 'incident', label: 'Vorfall' },
          ].map((k) => (
            <button
              key={k.id}
              onClick={() => setKind(k.id)}
              className={`py-3 rounded-lg text-sm font-semibold transition-colors ${
                kind === k.id ? 'bg-yellow-500 text-black' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
              }`}
              data-testid={`monteur-status-${k.id}`}
            >
              {k.label}
            </button>
          ))}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="Kurze Beschreibung (optional)…" className="bg-gray-800 border-gray-700" data-testid="monteur-status-note" />
        <Button onClick={send} className="w-full bg-yellow-500 hover:bg-yellow-400 text-black font-bold py-6" data-testid="monteur-status-send">
          <Send className="h-5 w-5 mr-2" /> Meldung absenden
        </Button>
      </CardContent>
    </Card>
  );
};

export default MonteurProApp;
